import PropTypes from 'prop-types';
import { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { Formik } from 'formik';
import {
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput, Typography
} from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { deviceWritePermission } from '../../../../store/selectors/account.selector';
import isDevicePropEqual from '../../../../utils/isDeviceEqual';

const DevicesSip = ({ isReadonly, device, onUpdate, ...props }) => {
  const editPermission = useSelector(deviceWritePermission);

  return (
    <Formik
      {...props}
      enableReinitialize
      initialValues={device.sip || { host: '', port: '', number: '', password: '' }}
      validationSchema={
        Yup.object().shape({
          // name: Yup.string().max(255).required('Забыли указать название'),
          host: Yup.string().max(255).required('Забыли указать хост'),
          port: Yup.string().max(255).required('Забыли указать порт'),
          number: Yup.string().max(255).required('Забыли указать логин'),
          password: Yup.string().max(255).required('Забыли указать пароль'),
        })
      }
      onSubmit={() => {
        // nothing
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isValid,
        isSubmitting,
        touched,
        values,
        setFieldValue
      }) => (
        <form onSubmit={handleSubmit}>
          <Typography variant="h3" sx={{ mt: -1, mb: 3 }}>
            {'Параметры SIP#1 устройства ' + (device.macAddress || '')}
          </Typography>
          <Grid
            container
            spacing={3}
          >
            <Grid
              item
              xs={12}
              sm={6}
            >
              {/* host field */}
              <FormControl sx={{ m: 0, width: '100%' }} variant="outlined">
                <InputLabel
                  error={Boolean(touched.host && errors.host)}
                  htmlFor="outlined-sip-host"
                >
                  Хост
                </InputLabel>
                <OutlinedInput
                  style={{ backgroundColor: 'white' }}
                  error={Boolean(touched.host && errors.host)}
                  id="outlined-sip-host"
                  type="text"
                  autoFocus
                  value={values.host}
                  onChange={(event) => {
                    setFieldValue('host', event.target.value);
                    onUpdate({ sip: { ...values, host: event.target.value } });
                  }}
                  readOnly={!editPermission || isReadonly}
                  label="Хост"
                />
                {touched.host && errors.host && (
                  <FormHelperText error id="outlined-sip-host-error">
                    {errors.host}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
            >
              {/* port field */}
              <FormControl sx={{ m: 0, width: '100%' }} variant="outlined">
                <InputLabel
                  error={Boolean(touched.port && errors.port)}
                  htmlFor="outlined-sip-port"
                >
                  Порт
                </InputLabel>
                <OutlinedInput
                  style={{ backgroundColor: 'white' }}
                  error={Boolean(touched.port && errors.port)}
                  id="outlined-sip-port"
                  type="text"
                  value={values.port}
                  onChange={(event) => {
                    setFieldValue('port', event.target.value);
                    onUpdate({ sip: { ...values, port: event.target.value } });
                  }}
                  readOnly={!editPermission || isReadonly}
                  label="Порт"
                />
                {touched.port && errors.port && (
                  <FormHelperText error id="outlined-port-error">
                    {errors.port}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
            >
              {/* number field */}
              <FormControl sx={{ m: 0, width: '100%' }} variant="outlined">
                <InputLabel
                  error={Boolean(touched.number && errors.number)}
                  htmlFor="outlined-sip-number"
                >
                  Логин
                </InputLabel>
                <OutlinedInput
                  style={{ backgroundColor: 'white' }}
                  error={Boolean(touched.number && errors.number)}
                  id="outlined-sip-number"
                  type="text"
                  value={values.number}
                  onChange={(event) => {
                    setFieldValue('number', event.target.value);
                    onUpdate({ sip: { ...values, number: event.target.value } });
                  }}
                  readOnly={!editPermission || isReadonly}
                  label="Логин"
                />
                {touched.number && errors.number && (
                  <FormHelperText error id="outlined-number-error">
                    {errors.number}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
            >
              {/* password field */}
              <FormControl sx={{ m: 0, width: '100%' }} variant="outlined">
                <InputLabel
                  error={Boolean(touched.password && errors.password)}
                  htmlFor="outlined-sip-password"
                >
                  Пароль
                </InputLabel>
                <OutlinedInput
                  style={{ backgroundColor: 'white' }}
                  error={Boolean(touched.password && errors.password)}
                  id="outlined-sip-password"
                  type={values.showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={values.password}
                  onChange={(event) => {
                    setFieldValue('password', event.target.value);
                    onUpdate({ sip: { ...values, password: event.target.value } });
                  }}
                  readOnly={!editPermission || isReadonly}
                  endAdornment={(
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="показать пароль"
                        tabIndex={-1}
                        onClick={() => {
                          setFieldValue('showPassword', !values.showPassword);
                        }}
                        onMouseDown={(event) => {
                          event.preventDefault();
                        }}
                        edge="end"
                      >
                        {values.showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  )}
                  label="Пароль"
                />
                {touched.password && errors.password && (
                  <FormHelperText error id="outlined-password-error">
                    {errors.password}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
};

DevicesSip.propTypes = {
  isReadonly: PropTypes.bool,
  device: PropTypes.object,
  onUpdate: PropTypes.func.isRequired,
};

DevicesSip.defaultProps = {
  isReadonly: false,
  device: null,
}

export default memo(DevicesSip, isDevicePropEqual);
